import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { CoachDetail } from "../../../../../../../../api/services/common/models";
import {
  FunctionCoach,
  IntakeDetail,
} from "../../../../../../../../api/services/program/models";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTypography,
} from "../../../../../../../../components";
import {
  INTAKE_SELECTION_SITE_CORE_KEY_VALUE,
  ROLE_IDS,
  TIMEOUT_300,
} from "../../../../../../../../helpers/constants";
import { isEmpty, isNotEmpty } from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import { convertFunctionsOrCoachToDropdown } from "../../../../../../helper";
import { ParticipantTabFormKey } from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface ParticipantIntakeSelectionFormProps {
  pageId: string;
  isEditProgram?: boolean;
  intakeDetails?: IntakeDetail;
  isDataSetProps?: boolean;
}

export const ParticipantIntakeSelection: FC<
  ParticipantIntakeSelectionFormProps
> = ({ pageId, isEditProgram, intakeDetails, isDataSetProps }) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const { control, getValues, watch, setValue } = useFormContext();
  const dispatch = useDispatch();
  const [coachBiosRequestedList, setCoachBiosRequestedList] = useState<
    FunctionCoach[]
  >([]);

  const coachBiosRequestedRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>();
  const coachBiosDeclinedRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>();
  const coachBiosSentRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>();
  const coachChemistryMeetingSelectedRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>();

  const [isIntakeStatusChanged, setIsIntakeStatusChanged] =
    useState<boolean>(false);

  const [coachBiosDeclinedList, setCoachBiosDeclinedList] = useState<
    FunctionCoach[]
  >([]);

  const [coachBiosSentList, setCoachBiosSentList] = useState<FunctionCoach[]>(
    [],
  );

  const [
    coachChemistryMeetingSelectedList,
    setCoachChemistryMeetingSelectedList,
  ] = useState<FunctionCoach[]>([]);

  const [coachSelectedList, setCoachSelectedList] = useState<FunctionCoach[]>(
    [],
  );

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.intakeSelection, key);
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programSiteCoreKeys.intakeSelection, key);
  };

  const {
    mutateAsync: getCoachList,
    data: coachesList,
    isPending: coachesListPending,
    isError: coachesListError,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    getCoachList({ roleIds: [ROLE_IDS.coach] });
  }, []);

  useEffect(() => {
    if (
      isNotEmpty(intakeDetails?.biosRequestedCoach) &&
      isEditProgram &&
      isNotEmpty(coachesList)
    ) {
      setTimeout(() => {
        coachBiosRequestedRef.current?.resetValues(
          intakeDetails?.biosRequestedCoach?.map(
            (item: CoachDetail) => item.id,
          ),
        );
      }, TIMEOUT_300);
    }
  }, [intakeDetails?.biosRequestedCoach, isEditProgram, coachesList]);

  const watchIntakeStatus = watch(ParticipantTabFormKey.intakeStatusId);
  const watchBioRequestedCoachIds = watch(
    ParticipantTabFormKey.biosRequestedCoachIds,
  );
  const watchBiosDeclinedCoachIds = watch(
    ParticipantTabFormKey.biosDeclinedCoachIds,
  );
  const watchCoachSelectedId = watch(
    ParticipantTabFormKey.coachSelectedCoachIds,
  );
  const watchCoachBiosSentIds = watch(ParticipantTabFormKey.biosSentCoachIds);
  const watchCoachChemistryMeetingSelectedIds = watch(
    ParticipantTabFormKey.chemistryMeetingSelectedCoachIds,
  );

  useEffect(() => {
    if (!isEditProgram) {
      setValue(
        ParticipantTabFormKey.intakeStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringNotStarted,
      );
    }
  }, [isEditProgram]);

  useEffect(() => {
    if (!isDataSetProps) return;
    const intakeStatus = getValues(ParticipantTabFormKey.intakeStatusId);
    const coachBiosRequestedIds = getValues(
      ParticipantTabFormKey.biosRequestedCoachIds,
    ) as string[];
    const coachSelectedId = getValues(
      ParticipantTabFormKey.coachSelectedCoachIds,
    );
    const coachBiosSentIds = getValues(
      ParticipantTabFormKey.biosSentCoachIds,
    ) as string[];

    const isDataGatheringNotStarted =
      intakeStatus ===
      INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringNotStarted;
    const isDataGatheringInProgress =
      intakeStatus ===
      INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringInProgress;

    if (isDataGatheringNotStarted) {
      setValue(
        ParticipantTabFormKey.bioStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.bioStatusPending,
      );
      return;
    }

    if (!isDataGatheringInProgress) return;

    // Priority order: Coach Selected > Bios Sent > Bios Requested
    if (isNotEmpty(coachSelectedId)) {
      setValue(
        ParticipantTabFormKey.bioStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.bioStatusBenchSent,
      );
    } else if (coachBiosSentIds?.length > 0) {
      setValue(
        ParticipantTabFormKey.bioStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.bioStatusSentManual,
      );
    } else if (coachBiosRequestedIds?.length > 0) {
      setValue(
        ParticipantTabFormKey.bioStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.bioStatusRequested,
      );
    }
  }, [
    watchIntakeStatus,
    watchCoachSelectedId,
    watchCoachBiosSentIds,
    watchBioRequestedCoachIds,
    isDataSetProps,
  ]);

  useEffect(() => {
    if (!isIntakeStatusChanged && !isEditProgram) return;
    const coachSelectedId = getValues(
      ParticipantTabFormKey.coachSelectedCoachIds,
    );
    if (isNotEmpty(coachSelectedId)) {
      setValue(
        ParticipantTabFormKey.intakeStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringComplete,
      );
    } else {
      setValue(
        ParticipantTabFormKey.intakeStatusId,
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringInProgress,
      );
    }
  }, [watchCoachSelectedId, isIntakeStatusChanged]);

  useEffect(() => {
    if (!isDataSetProps && isEmpty(coachesList)) return;
    const coachBiosRequestedIds = getValues(
      ParticipantTabFormKey.biosRequestedCoachIds,
    ) as string[];

    const selectedCoaches = (coachesList as FunctionCoach[])?.filter((coach) =>
      coachBiosRequestedIds?.includes(coach.id),
    );
    setCoachBiosRequestedList(selectedCoaches);
    cleanUpBiosDeclinedSelections();
  }, [
    watch(ParticipantTabFormKey.biosRequestedCoachIds),
    isDataSetProps,
    coachesList,
  ]);

  const cleanUpBiosDeclinedSelections = () => {
    const coachBiosRequestedIds = getValues(
      ParticipantTabFormKey.biosRequestedCoachIds,
    ) as string[];

    const coachBiosDeclinedIds = getValues(
      ParticipantTabFormKey.biosDeclinedCoachIds,
    ) as string[];

    // Check if any declined coaches are no longer in the requested list
    const validDeclinedIds = coachBiosDeclinedIds?.filter((id) =>
      coachBiosRequestedIds?.includes(id),
    );

    // If some declined coaches are no longer valid, update the form value
    setValue(ParticipantTabFormKey.biosDeclinedCoachIds, validDeclinedIds);
  };

  useEffect(() => {
    if (!isDataSetProps && isEmpty(coachBiosRequestedList)) return;
    const coachBiosDeclinedIds = getValues(
      ParticipantTabFormKey.biosDeclinedCoachIds,
    ) as string[];

    // Update coaches available for bios sent (exclude declined coaches)
    const availableCoachesForBiosSent =
      coachBiosDeclinedIds?.length > 0
        ? coachBiosRequestedList?.filter(
            (coach) => !coachBiosDeclinedIds.includes(coach.id),
          )
        : coachBiosRequestedList || [];

    setCoachBiosSentList(availableCoachesForBiosSent);
    cleanUpBiosSentSelections();
  }, [watchBiosDeclinedCoachIds, isDataSetProps, coachBiosRequestedList]);

  const cleanUpBiosSentSelections = () => {
    const coachBiosRequestedIds = getValues(
      ParticipantTabFormKey.biosRequestedCoachIds,
    ) as string[];

    const coachBiosSentIds = getValues(
      ParticipantTabFormKey.biosSentCoachIds,
    ) as string[];

    // Check if any declined coaches are no longer in the requested list
    const validBiosSentIds = coachBiosSentIds?.filter((id) =>
      coachBiosRequestedIds?.includes(id),
    );

    // If some declined coaches are no longer valid, update the form value
    setValue(ParticipantTabFormKey.biosSentCoachIds, validBiosSentIds);
  };
  useEffect(() => {
    if (!isDataSetProps && isEmpty(coachBiosRequestedList)) return;
    const coachBiosSentIds = getValues(
      ParticipantTabFormKey.biosSentCoachIds,
    ) as string[];

    // Update coaches available for bios declined (exclude sent coaches)
    const availableCoachesForBiosDeclined =
      coachBiosSentIds?.length > 0
        ? coachBiosRequestedList?.filter(
            (coach) => !coachBiosSentIds.includes(coach.id),
          )
        : coachBiosRequestedList || [];

    setCoachBiosDeclinedList(availableCoachesForBiosDeclined);

    const biosSentCoaches = coachBiosSentList?.filter((coach) =>
      coachBiosSentIds?.includes(coach.id),
    );

    setCoachChemistryMeetingSelectedList(biosSentCoaches);

    cleanUpChemistryMeetingSelections();
  }, [
    watchCoachBiosSentIds,
    isDataSetProps,
    coachBiosRequestedList,
    coachBiosSentList,
  ]);

  const cleanUpChemistryMeetingSelections = () => {
    const coachBiosSentIds = getValues(
      ParticipantTabFormKey.biosSentCoachIds,
    ) as string[];

    const chemistryMeetingSelectedIds = getValues(
      ParticipantTabFormKey.chemistryMeetingSelectedCoachIds,
    ) as string[];

    // Check if any declined coaches are no longer in the requested list
    const validChemistryMeetingIds = chemistryMeetingSelectedIds?.filter((id) =>
      coachBiosSentIds?.includes(id),
    );

    // If some declined coaches are no longer valid, update the form value
    setValue(
      ParticipantTabFormKey.chemistryMeetingSelectedCoachIds,
      validChemistryMeetingIds,
    );
  };

  useEffect(() => {
    if (!isDataSetProps && isEmpty(coachBiosSentList)) return;
    const coachChemistryMeetingSelectedIds = getValues(
      ParticipantTabFormKey.chemistryMeetingSelectedCoachIds,
    ) as string[];
    const chemistryMeetingSelectedIdsCoaches = (
      coachBiosSentList as FunctionCoach[]
    )?.filter((coach) => coachChemistryMeetingSelectedIds?.includes(coach.id));
    setCoachSelectedList(chemistryMeetingSelectedIdsCoaches);

    const coachSelectedId = getValues(
      ParticipantTabFormKey.coachSelectedCoachIds,
    );

    if (!coachChemistryMeetingSelectedIds.includes(coachSelectedId))
      setValue(ParticipantTabFormKey.coachSelectedCoachIds, "");
  }, [
    watchCoachChemistryMeetingSelectedIds,
    isDataSetProps,
    coachBiosSentList,
  ]);

  useEffect(() => {
    setTimeout(() => {
      coachBiosDeclinedRef?.current?.resetValues(
        getValues(ParticipantTabFormKey.biosDeclinedCoachIds) as string[],
      );
    }, TIMEOUT_300);
  }, [coachBiosDeclinedList]);

  useEffect(() => {
    setTimeout(() => {
      coachBiosSentRef?.current?.resetValues(
        getValues(ParticipantTabFormKey.biosSentCoachIds) as string[],
      );
    }, TIMEOUT_300);
  }, [coachBiosSentList]);

  useEffect(() => {
    setTimeout(() => {
      coachChemistryMeetingSelectedRef?.current?.resetValues(
        getValues(
          ParticipantTabFormKey.chemistryMeetingSelectedCoachIds,
        ) as string[],
      );
    }, TIMEOUT_300);
  }, [coachChemistryMeetingSelectedList]);

  const intakeStatus = watch(ParticipantTabFormKey.intakeStatusId);
  const biosSentIds = watch(ParticipantTabFormKey.biosSentCoachIds) as string[];
  const chemistryMeetingSelectedIds = watch(
    ParticipantTabFormKey.chemistryMeetingSelectedCoachIds,
  ) as string[];

  const isDataGatheringNotStarted = useMemo(() => {
    return (
      isEmpty(intakeStatus) ||
      intakeStatus ===
        INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringNotStarted
    );
  }, [intakeStatus]);

  const isDataGatheringInProgress = useMemo(() => {
    return (
      intakeStatus ===
      INTAKE_SELECTION_SITE_CORE_KEY_VALUE.intakeStatusDataGatheringInProgress
    );
  }, [intakeStatus]);

  const isDisabledCoachBiosRequested = isDataGatheringNotStarted;

  const isDisabledBiosDeclined = useMemo(() => {
    return (
      isDataGatheringNotStarted ||
      (isDataGatheringInProgress && isEmpty(coachBiosRequestedList))
    );
  }, [
    isDataGatheringNotStarted,
    isDataGatheringInProgress,
    coachBiosRequestedList,
  ]);

  const isDisabledBiosStatus = useMemo(() => {
    return isDataGatheringNotStarted;
  }, [isDataGatheringNotStarted, isDataGatheringInProgress, coachBiosSentList]);

  const isDisabledBiosSent = useMemo(() => {
    return (
      isDataGatheringNotStarted ||
      (isDataGatheringInProgress && isEmpty(coachBiosSentList))
    );
  }, [isDataGatheringNotStarted, isDataGatheringInProgress, coachBiosSentList]);

  const isDisabledChemistryMeetingStatus = useMemo(() => {
    return !biosSentIds?.length;
  }, [biosSentIds]);

  const isDisabledChemistryMeetingSelected = useMemo(() => {
    return !biosSentIds?.length;
  }, [biosSentIds]);

  const isDisabledCoachSelected = useMemo(() => {
    return !chemistryMeetingSelectedIds?.length;
  }, [chemistryMeetingSelectedIds]);

  const coachBiosRequestedDropdownOptions = useMemo(
    () => convertFunctionsOrCoachToDropdown(coachesList as FunctionCoach[]),
    [coachesList],
  );

  const coachBiosDeclinedDropdownOptions = useMemo(
    () => convertFunctionsOrCoachToDropdown(coachBiosDeclinedList),
    [coachBiosDeclinedList],
  );

  const coachBiosSentDropdownOptions = useMemo(
    () => convertFunctionsOrCoachToDropdown(coachBiosSentList),
    [coachBiosSentList],
  );

  const coachChemistryMeetingSelectedDropdownOptions = useMemo(
    () => convertFunctionsOrCoachToDropdown(coachChemistryMeetingSelectedList),
    [coachChemistryMeetingSelectedList],
  );

  const coachSelectedDropdownOptions = useMemo(
    () => convertFunctionsOrCoachToDropdown(coachSelectedList),
    [coachSelectedList],
  );
  useEffect(() => {
    if (coachesListError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }

    if (coachesListPending) {
      dispatch(
        toggleLoader({
          sponsoringPending: {
            isVisible: coachesListPending,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ sponsoringPending: { isVisible: false } }));
    };
  }, [coachesListError, coachesListPending]);

  const onChangeIntakeStatus = () => {
    setIsIntakeStatusChanged(true);
    coachBiosRequestedRef.current?.resetValues([]);
    coachBiosDeclinedRef.current?.resetValues([]);
    coachBiosSentRef.current?.resetValues([]);
    coachChemistryMeetingSelectedRef.current?.resetValues([]);
    setValue(ParticipantTabFormKey.bioStatusId, "");
    setValue(ParticipantTabFormKey.biosRequestedCoachIds, []);
    setValue(ParticipantTabFormKey.biosDeclinedCoachIds, []);
    setValue(ParticipantTabFormKey.biosSentCoachIds, []);
    setValue(ParticipantTabFormKey.chemistryMeetingSelectedCoachIds, []);
    setValue(ParticipantTabFormKey.coachSelectedCoachIds, "");
  };

  return (
    <MuiBox className="intake-selection-details" data-testid="intake-selection">
      <MuiTypography
        component="h2"
        className="sub-label-style"
        variant="h2SemiBold"
      >
        {getLabel(programSiteCoreKeys.intakeSection)}
      </MuiTypography>

      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.intakeStatusId}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.intakeStatus)}
              options={getDropdownValues(programSiteCoreKeys.intakeStatusList)}
              id={`${pageId}-${ParticipantTabFormKey.intakeStatusId}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              isMandatory
              onChange={onChangeIntakeStatus}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.biosRequestedCoachIds}
            ref={coachBiosRequestedRef}
          >
            <MuiMultiSelect
              label={getLabel(programSiteCoreKeys.coachBiosRequested)}
              options={coachBiosRequestedDropdownOptions}
              showSearch
              value=""
              showChip
              id={`${pageId}-${ParticipantTabFormKey.biosRequestedCoachIds}`}
              placeholder={commonLabels.selectPlaceHolder}
              disabled={isDisabledCoachBiosRequested}
              chipId={`${pageId}-${ParticipantTabFormKey.biosRequestedCoachIds}-chip`}
              dialogTitle={getLabel(programSiteCoreKeys.coachBiosRequested)}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.biosDeclinedCoachIds}
            ref={coachBiosDeclinedRef}
          >
            <MuiMultiSelect
              label={getLabel(programSiteCoreKeys.biosDeclined)}
              options={coachBiosDeclinedDropdownOptions}
              showSearch
              value=""
              showChip
              id={`${pageId}-${ParticipantTabFormKey.biosDeclinedCoachIds}`}
              placeholder={commonLabels.selectPlaceHolder}
              disabled={isDisabledBiosDeclined}
              chipId={`${pageId}-${ParticipantTabFormKey.biosDeclinedCoachIds}-chip`}
              dialogTitle={getLabel(programSiteCoreKeys.biosDeclined)}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiBox className="dash-separator-full-width" />

      <MuiTypography
        component="h2"
        className="sub-label-style"
        variant="h2SemiBold"
      >
        {getLabel(programSiteCoreKeys.bioStatusSection)}
      </MuiTypography>

      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.bioStatusId}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.bioStatus)}
              options={getDropdownValues(programSiteCoreKeys.bioStatusList)}
              id={`${pageId}-${ParticipantTabFormKey.bioStatusId}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              disabled={isDisabledBiosStatus}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.biosSentCoachIds}
            ref={coachBiosSentRef}
          >
            <MuiMultiSelect
              label={getLabel(programSiteCoreKeys.biosSent)}
              options={coachBiosSentDropdownOptions}
              showSearch
              value=""
              showChip
              id={`${pageId}-${ParticipantTabFormKey.biosSentCoachIds}`}
              placeholder={commonLabels.selectPlaceHolder}
              disabled={isDisabledBiosSent}
              chipId={`${pageId}-${ParticipantTabFormKey.biosSentCoachIds}-chip`}
              dialogTitle={getLabel(programSiteCoreKeys.biosSent)}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              declineAllPlaceHolder={commonLabels.declineAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>

      <MuiBox className="dash-separator-full-width" />
      <MuiTypography
        component="h2"
        className="sub-label-style"
        variant="h2SemiBold"
      >
        {getLabel(programSiteCoreKeys.chemistryMeetingSection)}
      </MuiTypography>
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.chemistryMeetingStatusId}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.chemistryMeetingStatus)}
              options={getDropdownValues(
                programSiteCoreKeys.chemistryMeetingStatusList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.chemistryMeetingStatusId}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              disabled={isDisabledChemistryMeetingStatus}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.chemistryMeetingSelectedCoachIds}
            ref={coachChemistryMeetingSelectedRef}
          >
            <MuiMultiSelect
              label={getLabel(programSiteCoreKeys.chemistryMeetingSelected)}
              options={coachChemistryMeetingSelectedDropdownOptions}
              showSearch
              value=""
              showChip
              id={`${pageId}-${ParticipantTabFormKey.chemistryMeetingSelectedCoachIds}`}
              placeholder={commonLabels.selectPlaceHolder}
              disabled={isDisabledChemistryMeetingSelected}
              chipId={`${pageId}-${ParticipantTabFormKey.chemistryMeetingSelectedCoachIds}-chip`}
              dialogTitle={getLabel(
                programSiteCoreKeys.chemistryMeetingSelected,
              )}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
              showDeclineAllOption
              showSelectAllOption
              onDeclineAll={(declined: boolean) => {
                setValue(
                  ParticipantTabFormKey.isCoachSelectionDeclined,
                  declined,
                );
              }}
              declinedValue={!!intakeDetails?.isCoachSelectionDeclined}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.coachSelectedCoachIds}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.coachSelected)}
              options={coachSelectedDropdownOptions}
              showSearch
              value=""
              id={`${pageId}-${ParticipantTabFormKey.coachSelectedCoachIds}`}
              placeholder={commonLabels.selectPlaceHolder}
              disabled={isDisabledCoachSelected}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
